import { type HTMLElement as NodeHtmlParserElement, parse } from 'node-html-parser'
import { codeToHtml } from 'shiki'
import { processLinkHrefAttribute } from '@/components/elementary/CkEditorRenderer/utils'
import { generateIdSlug, getUniqueId } from '@/lib/html'
import type { AppLocale } from '@/types/general'

export const processLinks = (doc: NodeHtmlParserElement | Document, locale: AppLocale) => {
	const links = doc.getElementsByTagName('a')
	for (const link of links) {
		const href = link.getAttribute('href')
		if (href?.startsWith('/')) {
			link.setAttribute('href', processLinkHrefAttribute(href, locale))
		}
	}
}

export const processImages = (doc: NodeHtmlParserElement | Document) => {
	addLazyLoading(doc, 'img')
}

export const processIframes = (doc: NodeHtmlParserElement | Document) => {
	addLazyLoading(doc, 'iframe')
}

const addLazyLoading = (doc: NodeHtmlParserElement | Document, tagName: 'img' | 'iframe') => {
	const elements = doc.getElementsByTagName(tagName)
	for (const element of elements) {
		if (!element?.getAttribute('loading')) {
			element?.setAttribute('loading', 'lazy')
		}
	}
}

/**
 * Highlights all code blocks in the given HTML document using shiki.
 *
 * This function searches for all <pre> elements, finds their child <code> tags,
 * extracts the code and language (from the class attribute, e.g., 'language-javascript'),
 * and uses shiki's codeToHtml to generate syntax-highlighted HTML.
 *
 * The original <pre> element is replaced with the highlighted HTML (which includes its own <pre> and <code> tags),
 * ensuring there are no nested <pre> tags in the output.
 *
 * @param doc - The root node of the parsed HTML document (node-html-parser's HTMLElement)
 */
export const processCodeBlocks = async (doc: NodeHtmlParserElement) => {
	const preElements = doc.querySelectorAll('pre')

	for (const pre of preElements) {
		const parsedCode = parse(pre.innerHTML)
		const codeNode = parsedCode.childNodes.find((node) => node.rawTagName === 'code') as
			| NodeHtmlParserElement
			| undefined
		if (codeNode && 'innerText' in codeNode) {
			const code = codeNode.textContent.trim()

			const classAttr = codeNode.getAttribute('class') || ''
			const langMatch = classAttr.match(/language-([\w-]+)/)
			const rawLang = langMatch?.[1]
			const lang = !rawLang || rawLang === 'null' ? 'plain' : rawLang
			const highlighted = await codeToHtml(code, { lang, theme: 'github-light' })

			// Parse the highlighted HTML to modify it
			const highlightedDoc = parse(highlighted)
			const newPreElement = highlightedDoc.querySelector('pre')

			// Function to escape <script> and <h.> tags to avoid parsing issues. It is decoded in the onclick handler of the copy button.
			const base64EncodedCode = Buffer.from(code, 'utf-8').toString('base64')

			if (newPreElement) {
				const wrapper = parse(`<div class="code-block-wrapper relative group"></div>`)
				// Create copy button with onclick handler
				// code should be extracted from the div, otherwise there are serialization issues
				const copyButton = parse(`
									<button 
										class="copy-code-btn cursor-pointer absolute top-2 right-2 px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded opacity-0 group-hover:opacity-75 hover:!opacity-100 transition-opacity z-10"
										data-code-base64="${base64EncodedCode}"
										onclick="
											const base64EncodedCode = this.getAttribute('data-code-base64');
											if (base64EncodedCode && navigator.clipboard) {
											const binaryString = atob(base64EncodedCode);
											const byteArray = Uint8Array.from(binaryString, (character) => character.charCodeAt(0));
												const originalCode = new TextDecoder().decode(byteArray);
												navigator.clipboard.writeText(originalCode).then(() => {
													const defaultIcon = this.querySelector('.copy-default');
													const successIcon = this.querySelector('.copy-success');
													successIcon.style.display = 'block';
													defaultIcon.style.display = 'none';
													setTimeout(() => { successIcon.style.display = 'none'; defaultIcon.style.display = 'block'; }, 2000);
												}).catch(() => { console.error('Failed to copy code'); });
											}
											"
											aria-label="Copy code"
											type="button"
											className="text-neutral-500"
										>
											<svg class="copy-default size-6" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 24 24"><path fill="currentColor" d="M 4 2 C 2.895 2 2 2.895 2 4 L 2 17 C 2 17.552 2.448 18 3 18 C 3.552 18 4 17.552 4 17 L 4 4 L 17 4 C 17.552 4 18 3.552 18 3 C 18 2.448 17.552 2 17 2 L 4 2 z M 8 6 C 6.895 6 6 6.895 6 8 L 6 20 C 6 21.105 6.895 22 8 22 L 20 22 C 21.105 22 22 21.105 22 20 L 22 8 C 22 6.895 21.105 6 20 6 L 8 6 z M 8 8 L 20 8 L 20 20 L 8 20 L 8 8 z"></path></svg>
											<svg class="copy-success hidden size-6" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 50 50" width="50px" height="50px"><path fill="currentColor" d="M 41.9375 8.625 C 41.273438 8.648438 40.664063 9 40.3125 9.5625 L 21.5 38.34375 L 9.3125 27.8125 C 8.789063 27.269531 8.003906 27.066406 7.28125 27.292969 C 6.5625 27.515625 6.027344 28.125 5.902344 28.867188 C 5.777344 29.613281 6.078125 30.363281 6.6875 30.8125 L 20.625 42.875 C 21.0625 43.246094 21.640625 43.410156 22.207031 43.328125 C 22.777344 43.242188 23.28125 42.917969 23.59375 42.4375 L 43.6875 11.75 C 44.117188 11.121094 44.152344 10.308594 43.78125 9.644531 C 43.410156 8.984375 42.695313 8.589844 41.9375 8.625 Z"/></svg>
										</button>
								`)

				// Add the pre element and button to the wrapper
				const wrapperElement = wrapper.firstChild as NodeHtmlParserElement
				wrapperElement?.appendChild(newPreElement)
				wrapperElement?.appendChild(copyButton)

				// Replace the original pre element with the entire wrapper element
				pre.replaceWith(wrapperElement)
			} else {
				// Fallback if parsing fails
				pre.replaceWith(highlighted)
			}
		}
	}
}

export const applyContentAttributes = (
	element: NodeHtmlParserElement | HTMLElement,
	textContent: string,
	usedIds: Set<string>,
) => {
	const existingId = element.getAttribute('id')
	if (existingId) {
		element.removeAttribute('id')
	}
	const baseId = generateIdSlug(textContent)
	const uniqueId = getUniqueId(baseId, usedIds)
	usedIds.add(uniqueId)
	element.setAttribute('id', uniqueId)
}

export const processFontSizes = (element: NodeHtmlParserElement | Element, tagName: string) => {
	for (const child of element.childNodes) {
		if (
			child &&
			typeof child === 'object' &&
			'getAttribute' in child &&
			typeof child.getAttribute === 'function'
		) {
			const style = child?.getAttribute('style')
			if (style?.includes('font-size')) {
				const existingStyle = element.getAttribute('style') || ''
				const fontSizeMatch = style.match(/font-size\s*:\s*([^;]+);?/)

				const updatedStyle =
					`${existingStyle}; font-size: ${fontSizeMatch?.[1]?.trim() ?? 'unset'} !important; line-height: ${tagName.startsWith('h') ? 1.3 : 1.5} !important;`.trim()
				element.setAttribute('style', updatedStyle)
				break
			}
		}
	}
}

export const processTagNames = (doc: NodeHtmlParserElement | Document, usedIds: Set<string>) => {
	const tagNames = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p']
	for (const tagName of tagNames) {
		const elements = doc.getElementsByTagName(tagName)
		for (const element of elements) {
			element.classList.add(`typo-${tagName}`)
			processFontSizes(element, tagName)
			if (tagName.startsWith('h')) {
				if (typeof window === 'undefined') {
					processTagNamesSSR(element as NodeHtmlParserElement, usedIds)
				} else {
					processTagNamesCSR(element as HTMLElement, usedIds)
				}
			}
		}
	}
}

const processTagNamesSSR = (element: NodeHtmlParserElement, usedIds: Set<string>) => {
	const textContent = element.text?.trim()
	if (textContent) {
		applyContentAttributes(element, textContent, usedIds)
	}
}

const processTagNamesCSR = (element: HTMLElement, usedIds: Set<string>) => {
	const textContent = element.textContent?.trim()
	if (textContent) {
		applyContentAttributes(element, textContent, usedIds)
	}
}
